import _ from 'lodash';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDebouncedCallback } from 'use-debounce';
import { Box, Flex } from 'reflexbox';
import { FormikProvider, useFormik } from 'formik';
import styled from 'styled-components';
import { Button, Callout, Heading, Paragraph, Subheading, Title } from '@treams/treams-ui';

import * as graphql from 'generated/graphql';
import useLocalize from 'packages/kakapo-ui-application/hooks/useLocalize';
import QuillText from 'packages/kakapo-ui-kit/components/editors/QuillText';
import { SectionTitle } from 'packages/kakapo-ui-kit/components/common/Headings';
import useErrorBoundary from 'packages/kakapo-ui-application/hooks/useErrrorBoundary';
import EvaluationSubmissionUnsign from 'packages/kakapo-ui-application/pages/evaluation-submissions/EvaluationSubmissionUnsign';
import ConversationFooter from 'packages/kakapo-ui-application/pages/evaluation-submissions/ConversationFooter';
import EvaluationSubmissionScore from 'packages/kakapo-ui-application/pages/evaluation-submissions/EvaluationSubmissionScore';
import EvaluationNotes from './notes/EvaluationNotes';
import EvaluationSubmissionItem from './EvaluationSubmissionItem';
import EvaluationSubmissionSection from 'packages/kakapo-ui-application/pages/evaluation-submissions/EvaluationSubmissionSection';
import EvaluationSubmissionProgress from 'packages/kakapo-ui-application/pages/evaluation-submissions/EvaluationSubmissionProgress';
import EvaluationParties from 'packages/kakapo-ui-application/pages/evaluation-submissions/EvaluationParties';
import EvaluationSubmissionRequired from 'packages/kakapo-ui-application/pages/evaluation-submissions/EvaluationSubmissionRequired';
import EvaluationSubmissionArchived from 'packages/kakapo-ui-application/pages/evaluation-submissions/EvaluationSubmissionArchived';
import EvaluationErrorCard from 'packages/kakapo-ui-application/pages/evaluation-submissions/EvaluationErrorCard';
import EvaluationSubmissionSignatureModal from 'packages/kakapo-ui-application/pages/evaluation-submissions/EvaluationSubmissionSignatureModal';
import useDetectPrint from '../hooks/useDetectPrint';

const MomentTitle = styled.p`
	color: ${(props) => props.theme.colors.slateBlue};
	font-size: 14px;
	line-height: 24px;
	font-style: italic;
`;

function ConfirmReminderCallout(props: {
	submission: graphql.EvaluationSubmission;
	saving: boolean;
	acquire: () => Promise<void>;
	onErrors: (errors: Set<string>) => void;
}) {
	const { t } = useTranslation();

	const [signModal, setSignModal] = useState(false);

	return (
		<>
			<Callout color="blue" mb={3}>
				<Box>
					<Box mb={2}>
						<Subheading>{t('conversations.confirm.reminder.title')}</Subheading>
						<Paragraph>{t('conversations.confirm.reminder.description')}</Paragraph>
					</Box>
					<Flex>
						<Button
							type="card"
							title={t('conversations.confirm.reminder.button')}
							small
							mr={2}
							onClick={() => setSignModal(true)}
						/>
					</Flex>
				</Box>
			</Callout>
			{signModal && (
				<EvaluationSubmissionSignatureModal
					submission={props.submission}
					saving={props.saving}
					acquire={props.acquire}
					onErrors={props.onErrors}
					onDismiss={() => setSignModal(false)}
				/>
			)}
		</>
	);
}

export default function EvaluationSubmission(props: {
	submission: graphql.EvaluationSubmissionFragment;
	onRefresh: () => void;
	isSidebarOpen: boolean;
}) {
	const { localize } = useLocalize();
	const isPrinting = useDetectPrint();

	const [lastSaved, setLastSaved] = useState(new Date());

	const sections = props.submission.viewer.sections.map((section) => ({
		items: section.items.map(
			(item) =>
				item && {
					type: item.type,
					stars: item.stars,
					openQuestion: item.openQuestion,
					sentiment: item.sentiment,
					multipleChoice: item.multipleChoice,
					competency: item.competency,
					reviewGoals: item.reviewGoals?.map((goal) => _.omit(goal, '__typename')),
				},
		),
	}));

	const formik = useFormik({ initialValues: { sections }, onSubmit: () => {} });

	const formikRef = useRef(formik);
	formikRef.current = formik;

	const dirty = useRef<boolean>(false);
	const busy = useRef(false);

	const [commit, { loading: updateLoading }] = graphql.useUpdateEvaluationSubmissionMutation({});

	const [error, attempt] = useErrorBoundary();

	const callbacks = useRef<(() => void)[]>([]).current;

	const onSave = attempt(async () => {
		if (!props.submission.viewer.viewerCanUpdate) {
			return;
		}

		await commit({
			variables: {
				input: {
					id: props.submission.id,
					sections: formikRef.current.values.sections,
					signed: false,
				},
			},
		});
		setLastSaved(new Date());
		formik.resetForm({ values: formikRef.current.values });
	});

	const onSaveGuard = async () => {
		dirty.current = true;

		if (busy.current) {
			return;
		}

		dirty.current = false;
		busy.current = true;

		await onSave();

		busy.current = false;

		if (dirty.current) {
			onSaveGuard();
		} else {
			for (const callback of callbacks) {
				callback();
			}

			callbacks.length = 0;
		}
	};

	const acquire = async (): Promise<void> => {
		return new Promise((resolve) => {
			if (!busy.current) {
				return resolve();
			} else {
				callbacks.push(resolve);
			}
		});
	};

	const autoSave = useDebouncedCallback(onSaveGuard, 1000);
	const [errors, setErrors] = useState(new Set<string>());
	const useAutosave = !props.submission.viewer.hasShared;

	useEffect(() => {
		if (useAutosave && formik.dirty) {
			autoSave();
		}
	}, [autoSave, formik.dirty, useAutosave]);

	const { t } = useTranslation();

	const filteredSections = props.submission.evaluationForm.sections
		.map((section, index) => ({ section, index }))
		.filter(({ section }) =>
			section.items.some((item) => {
				if (
					item.type === graphql.EvaluationSectionItemType.FinalScore ||
					item.type === graphql.EvaluationSectionItemType.Signature
				) {
					return false;
				}

				if (props.submission.employee.hasShared || props.submission.manager.hasShared) {
					return true;
				}

				return (
					item.fillInBy === graphql.EvaluationFillInBy.Both ||
					item.fillInBy.valueOf() === props.submission.viewer.party.valueOf()
				);
			}),
		);

	return (
		<FormikProvider value={formik}>
			<Flex justifyContent="center" flexDirection="row" flexWrap="wrap-reverse">
				<Box width={isPrinting ? 1 : 10 / 12} maxWidth={1000}>
					{error && (
						<EvaluationErrorCard
							error={error}
							submission={props.submission as graphql.EvaluationSubmission}
							lastSaved={lastSaved}
						/>
					)}
				</Box>
			</Flex>
			<Flex justifyContent="center" flexDirection="row" flexWrap="wrap-reverse" mb={4}>
				<Box width={isPrinting ? 1 : 10 / 12} maxWidth={1000}>
					{props.submission.viewer.viewerCanShare && (
						<Callout
							mb={3}
							title={t('conversation.process.callout.share.title')}
							description={t('conversation.process.callout.share.description')}
							color={'blue'}
						/>
					)}
					{props.submission.viewer.requiresInput &&
						!props.submission.viewer.viewerCanShare &&
						!props.submission.viewer.viewerCanSign &&
						props.submission.viewer.viewerCanUpdate && (
							<Callout
								mb={3}
								title={t('conversation.process.callout.nosignature.title')}
								description={t('conversation.process.callout.nosignature.description')}
								color={'blue'}
							/>
						)}
					{!props.submission.viewer.requiresInput && !props.submission.viewer.viewerCanSign && (
						<Callout
							mb={3}
							title={t('conversation.process.callout.noinput.title')}
							description={t('conversation.process.callout.noinput.description')}
							color={'blue'}
						/>
					)}
					{!props.submission.viewer.viewerCanUpdate && !props.submission.viewer.viewerCanSign && (
						<Callout
							mb={3}
							title={t('conversation.process.callout.aftersigning.title')}
							description={t('conversation.process.callout.aftersigning.description')}
							color={'blue'}
						/>
					)}
					{errors.size > 0 && (
						<EvaluationSubmissionRequired>
							{t('evaluation.submission.missingAnswers')}
						</EvaluationSubmissionRequired>
					)}
					{!isPrinting && props.submission.isArchived && (
						<EvaluationSubmissionArchived>
							{t('evaluations.submission.archivedByEitherParty')}
						</EvaluationSubmissionArchived>
					)}
					{props.submission.viewer.viewerCanSign && (
						<ConfirmReminderCallout
							submission={props.submission as graphql.EvaluationSubmission}
							acquire={acquire}
							saving={updateLoading}
							onErrors={setErrors}
						/>
					)}
					<Box mb={4}>
						<Title mb={2}>{localize(props.submission.evaluationForm.title).text}</Title>
						{props.submission.moment && (
							<MomentTitle>
								{t('evaluations.submission.momentTitle', {
									title: localize(props.submission.moment.momentTemplate.title).text,
								})}
							</MomentTitle>
						)}
						{props.submission.evaluationForm.description && (
							<Paragraph>
								<QuillText quill={localize(props.submission.evaluationForm.description).text} />
							</Paragraph>
						)}
					</Box>
					<Box mb={4}>
						<Heading mb={2}>{t('evaluations.submission.title')}</Heading>
						<EvaluationParties submission={props.submission as graphql.EvaluationSubmission} />
					</Box>
					{!isPrinting && props.submission.viewer.viewerCanUpdate && (
						<Box mb={4}>
							<EvaluationSubmissionProgress
								submission={props.submission as graphql.EvaluationSubmission}
							/>
						</Box>
					)}
					{filteredSections.map(({ section, index: sectionIndex }) => (
						<EvaluationSubmissionSection key={sectionIndex} mb={4}>
							{section.items.map((_, index) => (
								<EvaluationSubmissionItem
									key={index}
									section={sectionIndex}
									item={index}
									submission={props.submission as graphql.EvaluationSubmission}
									error={errors.has(`sections[${sectionIndex}].items[${index}]`)}
									onSave={onSaveGuard}
									onNotesChange={props.onRefresh}
									isSaving={updateLoading}
								/>
							))}
						</EvaluationSubmissionSection>
					))}
					<EvaluationSubmissionScore
						submission={props.submission as graphql.EvaluationSubmission}
						saving={updateLoading}
					/>
					{(props.submission.viewer.hasShared || !props.submission.viewer.viewerCanUpdate) &&
						props.submission.comments.some(
							(comment) => !comment.metadata?.some(({ key }) => key === 'formItemID'),
						) && (
							<Box mb={4}>
								<Box mb={2}>
									<SectionTitle>{t('evaluation.submission.notes.title')}</SectionTitle>
								</Box>
								<EvaluationNotes
									submission={props.submission as graphql.EvaluationSubmission}
									onNotesChange={props.onRefresh}
								/>
							</Box>
						)}
					{!isPrinting && (
						<>
							<EvaluationSubmissionUnsign
								submission={props.submission as graphql.EvaluationSubmission}
							/>
							<ConversationFooter
								submission={props.submission as graphql.EvaluationSubmission}
								acquire={acquire}
								attempt={attempt}
								save={() => onSaveGuard()}
								saving={updateLoading}
								onErrors={setErrors}
								error={error}
								lastSaved={lastSaved}
								isSidebarOpen={props.isSidebarOpen}
							/>
						</>
					)}
				</Box>
			</Flex>
		</FormikProvider>
	);
}
